import React, { useState } from 'react';
import { X, Download, ZoomIn, ZoomOut } from 'lucide-react';

interface PDFViewerProps {
    url: string;
    setUrl: (url: string) => void;
    fileType?: string;
}

export default function PDFViewer({ url, setUrl, fileType }: PDFViewerProps) {
    const [showModal, setShowModal] = useState(true);
    const [scale, setScale] = useState(1.0);

    const handleClose = () => {
        setShowModal(false);
        setTimeout(() => setUrl(''), 200);
    };

    const handleZoomIn = () => setScale(prev => Math.min(prev + 0.2, 3.0));
    const handleZoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));

    if (!showModal) return null;

    const openPdfInNewTab = (pdfUrl: string) => {
        if (!pdfUrl) return;
        const encodedUrl = encodeURIComponent(url);
        const stringifiedUrl = JSON.stringify(encodedUrl);
        if (stringifiedUrl) {
            const customViewer = `/pdf-viewer?url=${stringifiedUrl}`;
            window.open(customViewer, "_blank", "noopener,noreferrer");
        }
        handleClose();
        return null;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-3 sm:px-6 py-6">

            <div
                className="absolute inset-0 bg-black/40"
                onClick={handleClose}
            />


            <div className="relative w-full max-w-[95%] sm:max-w-2xl lg:max-w-5xl h-[90vh] sm:h-full max-h-[90vh] sm:max-h-[85vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden">


                <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-100">
                    {fileType === 'Photo' && (
                        <div className="flex items-center space-x-1 sm:space-x-2">
                            <button onClick={handleZoomOut} className="p-1.5 text-gray-400 hover:text-gray-600">
                                <ZoomOut className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                            <span className="text-xs sm:text-sm text-gray-500 min-w-[40px] text-center">
                                {Math.round(scale * 100)}%
                            </span>
                            <button onClick={handleZoomIn} className="p-1.5 text-gray-400 hover:text-gray-600">
                                <ZoomIn className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                        </div>
                    )}

                    <div className="flex items-center space-x-1 sm:space-x-2">
                        <a href={url} download className="p-1.5 text-gray-400 hover:text-gray-600">
                            <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                        </a>
                        <button onClick={handleClose} className="p-1.5 text-gray-400 hover:text-gray-600">
                            <X className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto p-3 sm:p-6 flex justify-center items-center">
                    {fileType === 'Pdf' ? (
                        openPdfInNewTab(url)
                    ) : fileType === 'Photo' ? (
                        <img
                            src={url}
                            alt="Preview"
                            className="shadow-sm rounded object-contain max-w-full max-h-full"
                            style={{ transform: `scale(${scale})` }}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
}
